import { Component, ElementRef, OnInit, ViewChild, inject, DestroyRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { FiltersService } from 'src/app/core/services/filters/filters.service';
import { CommunityService } from '../../services/community/community.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-community-list',
    templateUrl: './community-list.component.html',
    styleUrl: './community-list.component.scss',
    standalone: false
})
export class CommunityListComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  spinner: boolean = true;
  searchValue: string = '';
  page: number = 1;
  page_size: number = 20;
  totalRecords: number = 0;
  config: any;
  filterPage: string = 'COMMUNITY';
  pageTitle: string = 'المجتمعات';
  filterData: any;
  filterDataInit: any = {};
  filterParamsUrl: Record<string, string | string[]> = {};
  communities: any[] = [];
  currentItemsPerPage: number = 0;
  imgDefault: string = '/assets/images/svg/sort-default.svg';
  imgASC: string = '/assets/images/svg/sort-up.svg';
  imgDesc: string = '/assets/images/svg/sort-down.svg';
  orderingState: any = {
    title: null,
    code: null,
  };
  ordering: any[] = [];
  
  @ViewChild('goUp', { static: true }) contentPage!: ElementRef;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private titleService: Title,
    private filter: FiltersService,
    private communityService: CommunityService
  ) {
    this.config = {
      id: 'communityPagination',
      itemsPerPage: this.page_size,
      currentPage: this.page,
      totalItems: this.totalRecords,
    };
  }

  ngOnInit(): void {
    this.titleService.setTitle(this.pageTitle + ' | عاين');
    this.getFiltersData();
    
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      if (params['page'] && params['page_size']) {
        this.page = Number(params['page']);
        this.page_size = Number(params['page_size']);
        
        const { page, page_size, ...restparams } = params;
        this.filterParamsUrl = { ...restparams };
        this.filterDataInit = { ...restparams };
        
        this.getCommunities();
      } else {
        this.router.navigate([], {
          queryParams: { page: 1, page_size: 20 },
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  getFiltersData(): void {
    const params = {
      filter_page: this.filterPage
    };
    
    this.filter.getFiltersData(params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.filterData = data;
      },
      error: (error) => {
        console.error('Error loading filters:', error);
      }
    });
  }

  getCommunities(): void {
    this.spinner = true;
    const params: any = {
      page: this.page,
      page_size: this.page_size,
      ...this.filterParamsUrl
    };

    if (this.searchValue) {
      params.query = this.searchValue;
    }

    this.communityService.getCommunities(params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.communities = response.data;
        this.totalRecords = response.meta.pagination.count;
        this.config['totalItems'] = this.totalRecords;
        this.currentItemsPerPage = this.communities.length;
        this.spinner = false;
      },
      error: (error) => {
        this.spinner = false;
      }
    });
  }

  filterFormValues(event: any): void {
    this.filterParamsUrl = event.filterParamsUrl;
    this.spinner = true;
    this.page = 1;
    this.config = {
      ...this.config,
      currentPage: 1
    };
    this.router.navigate([], {
      queryParams: { page: 1, page_size: this.page_size, ...this.filterParamsUrl },
      queryParamsHandling: '',
    });
  }

  quickSearch(searchValue: string): void {
    this.searchValue = searchValue;
    this.page = 1;
    this.config = {
      ...this.config,
      currentPage: 1
    };
    this.router.navigate([], {
      queryParams: { page: 1, query: searchValue || null },
      queryParamsHandling: 'merge',
    });
  }

  goToAdd(): void {
    this.router.navigateByUrl('/pages/community/new');
    this.titleService.setTitle('إضافة مجتمع جديد | عاين');
  }

  goToDetails(id: number, title: string): void {
    this.router.navigateByUrl(`/pages/community/community-details/${id}`);
    this.titleService.setTitle(title + ' | عاين');
  }

  editCommunity(event: Event, id: number): void {
    event.stopPropagation();
    this.router.navigateByUrl(`/pages/community/community-details/${id}`);
    this.titleService.setTitle('تعديل مجتمع | عاين');
  }

  viewCommunity(event: Event, id: number): void {
    event.stopPropagation();
    this.router.navigateByUrl(`/pages/community/community-view/${id}`);
    this.titleService.setTitle('عرض مجتمع | عاين');
  }

  pageChanged(event: any): void {
    this.page = event;
    this.config = {
      ...this.config,
      currentPage: event
    };
    this.router.navigate([], {
      queryParams: { page: event },
      queryParamsHandling: 'merge',
    });
    this.contentPage?.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  pageSizeChanged(event: any): void {
    this.page_size = event;
    this.page = 1;
    this.config = {
      ...this.config,
      itemsPerPage: event,
      currentPage: 1
    };
    this.router.navigate([], {
      queryParams: { page: 1, page_size: event },
      queryParamsHandling: 'merge',
    });
  }

  toggleSorting(colName: any, img: any): void {
    this.ordering = [];
    switch (this.orderingState[colName]) {
      case null:
        this.orderingState[colName] = false;
        img.src = this.imgASC;
        break;

      case false:
        this.orderingState[colName] = true;
        img.src = this.imgDesc;
        break;

      case true:
        this.orderingState[colName] = null;
        img.src = this.imgDefault;
        break;
    }
    
    for (const [key, value] of Object.entries(this.orderingState)) {
      switch (value) {
        case false:
          this.ordering.push(key);
          break;
        case true:
          this.ordering.push('-' + key);
          break;
      }
    }
    
    // Update filterParamsUrl with ordering
    if (this.ordering.length > 0) {
      this.filterParamsUrl = {
        ...this.filterParamsUrl,
        ordering: this.ordering.join(',')
      };
    } else {
      const { ordering, ...rest } = this.filterParamsUrl;
      this.filterParamsUrl = rest;
    }
    
    this.getCommunities();
  }
}
